import { apiClient } from "../client";

export interface Invitation {
  id: number;
  siteId: number;
  userId: number | null;
  email: string;
  role: string;
  status: string;
  dateJoined: string | null;
  invitedBy: number;
  invitationAcceptedAt: string | null;
  createdAt: string;
  updatedAt: string;
  invitationToken?: string;
}

export interface InviteUsersData {
  emails: string[];
  role: string;
}

export interface InviteUsersResponse {
  message: string;
  invitations: Invitation[];
  skipped?: Array<{ email: string; reason: string }>;
}

export interface GetMembersResponse {
  members: Invitation[];
}

export interface UpdateMemberRoleData {
  role: string;
}

export interface UpdateMemberRoleResponse {
  message: string;
  member: Invitation;
}

export interface UpdateMemberStatusData {
  status: string;
}

export interface UpdateMemberStatusResponse {
  message: string;
  member: Invitation;
}

export interface RemoveMemberResponse {
  message: string;
}

export interface AcceptInvitationResponse {
  message: string;
  invitation: Invitation;
}

export interface RejectInvitationResponse {
  message: string;
}

export interface GetUserInvitationsResponse {
  invitations: Invitation[];
}

export interface InvitationDetails {
  invitation: {
    id: number;
    email: string;
    role: string;
    status: string;
    site: {
      id: number;
      name: string;
      systemCode: string;
      iconColor: string;
      city: string;
      state: string;
      country: string;
      memberCount?: number;
    };
    organization: {
      id: number;
      name: string;
      slug: string;
      logo?: string;
    };
    inviter: {
      name?: string;
      email: string;
    } | null;
  };
  userEmail?: string;
  emailMatches?: boolean;
  canAccept?: boolean;
  verification?: {
    userEmail: string;
    emailMatches: boolean;
    canAccept: boolean;
    userIsLoggedIn: boolean;
  };
  message?: string;
  expiresAt?: Date;
}

export interface AcceptInvitationByTokenResponse {
  message: string;
  site: {
    id: number;
    name: string;
    systemCode: string;
  };
}

export interface ApiError {
  message: string | string[];
  error?: string;
  statusCode?: number;
  code: string;
}

export interface AxiosErrorResponse {
  response?: {
    data: ApiError;
  };
  message: string;
}

export interface OrganizationMember {
  id: number;
  email: string;
  name: string | null;
  role: string;
  status: string;
  dateJoined: string | null;
  createdAt: string;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface GetOrganizationMembersResponse {
  message: string;
  organization: {
    id: number;
    name: string;
    slug: string;
  };
  members: OrganizationMember[];
  pagination: PaginationMeta;
}

export class InvitationService {
  static async getOrganizationMembers(
    organizationId: number,
    page?: number,
    limit?: number,
    search?: string,
    status?: string,
    role?: string,
  ): Promise<GetOrganizationMembersResponse> {
    try {
      const params = new URLSearchParams();
      if (page !== undefined) params.append("page", page.toString());
      if (limit !== undefined) params.append("limit", limit.toString());
      if (search) params.append("search", search);
      if (status) params.append("status", status);
      if (role) params.append("role", role);

      const queryString = params.toString();
      const url = queryString
        ? `/organizations/${organizationId}/members?${queryString}`
        : `/organizations/${organizationId}/members`;

      return await apiClient.get<GetOrganizationMembersResponse>(url);
    } catch (error: unknown) {
      const axiosError = error as AxiosErrorResponse;
      const errorCode =
        (axiosError.response?.data?.code as string) || "UNKNOWN_ERROR";
      const errorMessage =
        (axiosError.response?.data?.message as string) ||
        "Failed to get organization members";

      throw {
        code: errorCode,
        message: errorMessage,
      };
    }
  }

  /**
   * Invite users to a site
   * Cookies are automatically sent by apiClient with withCredentials: true
   */
  static async inviteUsersToSite(
    organizationId: number,
    siteId: number,
    inviteData: InviteUsersData,
  ): Promise<InviteUsersResponse> {
    try {
      return await apiClient.post<InviteUsersResponse>(
        `/organizations/${organizationId}/sites/${siteId}/invite`,
        inviteData,
      );
    } catch (error: unknown) {
      const axiosError = error as AxiosErrorResponse;
      const errorCode =
        (axiosError.response?.data?.code as string) || "UNKNOWN_ERROR";
      const errorMessage =
        (axiosError.response?.data?.message as string) ||
        "Failed to invite users";

      throw {
        code: errorCode,
        message: errorMessage,
      };
    }
  }

  /**
   * Get all members for a site
   * Cookies are automatically sent by apiClient with withCredentials: true
   */
  static async getSiteMembers(
    organizationId: number,
    siteId: number,
  ): Promise<GetMembersResponse> {
    try {
      return await apiClient.get<GetMembersResponse>(
        `/organizations/${organizationId}/sites/${siteId}/members`,
      );
    } catch (error: unknown) {
      const axiosError = error as AxiosErrorResponse;
      const errorCode =
        (axiosError.response?.data?.code as string) || "UNKNOWN_ERROR";
      const errorMessage =
        (axiosError.response?.data?.message as string) ||
        "Failed to get members";

      throw {
        code: errorCode,
        message: errorMessage,
      };
    }
  }

  /**
   * Update member role
   * Cookies are automatically sent by apiClient with withCredentials: true
   */
  static async updateMemberRole(
    organizationId: number,
    siteId: number,
    memberId: number,
    roleData: UpdateMemberRoleData,
  ): Promise<UpdateMemberRoleResponse> {
    try {
      return await apiClient.put<UpdateMemberRoleResponse>(
        `/organizations/${organizationId}/sites/${siteId}/members/${memberId}/role`,
        roleData,
      );
    } catch (error: unknown) {
      const axiosError = error as AxiosErrorResponse;
      const errorCode =
        (axiosError.response?.data?.code as string) || "UNKNOWN_ERROR";
      const errorMessage =
        (axiosError.response?.data?.message as string) ||
        "Failed to update member role";

      throw {
        code: errorCode,
        message: errorMessage,
      };
    }
  }

  /**
   * Update member status
   * Cookies are automatically sent by apiClient with withCredentials: true
   */
  static async updateMemberStatus(
    organizationId: number,
    siteId: number,
    memberId: number,
    statusData: UpdateMemberStatusData,
  ): Promise<UpdateMemberStatusResponse> {
    try {
      return await apiClient.put<UpdateMemberStatusResponse>(
        `/organizations/${organizationId}/sites/${siteId}/members/${memberId}/status`,
        statusData,
      );
    } catch (error: unknown) {
      const axiosError = error as AxiosErrorResponse;
      const errorCode =
        (axiosError.response?.data?.code as string) || "UNKNOWN_ERROR";
      const errorMessage =
        (axiosError.response?.data?.message as string) ||
        "Failed to update member status";

      throw {
        code: errorCode,
        message: errorMessage,
      };
    }
  }

  /**
   * Remove member from site
   * Cookies are automatically sent by apiClient with withCredentials: true
   */
  static async removeMember(
    organizationId: number,
    siteId: number,
    memberId: number,
  ): Promise<RemoveMemberResponse> {
    try {
      return await apiClient.delete<RemoveMemberResponse>(
        `/organizations/${organizationId}/sites/${siteId}/members/${memberId}`,
      );
    } catch (error: unknown) {
      const axiosError = error as AxiosErrorResponse;
      const errorCode =
        (axiosError.response?.data?.code as string) || "UNKNOWN_ERROR";
      const errorMessage =
        (axiosError.response?.data?.message as string) ||
        "Failed to remove member";

      throw {
        code: errorCode,
        message: errorMessage,
      };
    }
  }

  /**
   * Accept an invitation by ID
   * Cookies are automatically sent by apiClient with withCredentials: true
   */
  static async acceptInvitation(
    invitationId: number,
  ): Promise<AcceptInvitationResponse> {
    try {
      return await apiClient.post<AcceptInvitationResponse>(
        `/organizations/sites/invitations/${invitationId}/accept`,
        {},
      );
    } catch (error: unknown) {
      const axiosError = error as AxiosErrorResponse;
      const errorCode =
        (axiosError.response?.data?.code as string) || "UNKNOWN_ERROR";
      const errorMessage =
        (axiosError.response?.data?.message as string) ||
        "Failed to accept invitation";

      throw {
        code: errorCode,
        message: errorMessage,
      };
    }
  }

  /**
   * Reject an invitation by ID
   * Cookies are automatically sent by apiClient with withCredentials: true
   */
  static async rejectInvitation(
    invitationId: number,
  ): Promise<RejectInvitationResponse> {
    try {
      return await apiClient.post<RejectInvitationResponse>(
        `/organizations/sites/invitations/${invitationId}/reject`,
        {},
      );
    } catch (error: unknown) {
      const axiosError = error as AxiosErrorResponse;
      const errorCode =
        (axiosError.response?.data?.code as string) || "UNKNOWN_ERROR";
      const errorMessage =
        (axiosError.response?.data?.message as string) ||
        "Failed to reject invitation";

      throw {
        code: errorCode,
        message: errorMessage,
      };
    }
  }

  /**
   * Get user's pending invitations
   * Cookies are automatically sent by apiClient with withCredentials: true
   */
  static async getUserInvitations(): Promise<GetUserInvitationsResponse> {
    try {
      return await apiClient.get<GetUserInvitationsResponse>(
        `/users/invitations`,
      );
    } catch (error: unknown) {
      const axiosError = error as AxiosErrorResponse;
      const errorCode =
        (axiosError.response?.data?.code as string) || "UNKNOWN_ERROR";
      const errorMessage =
        (axiosError.response?.data?.message as string) ||
        "Failed to get user invitations";

      throw {
        code: errorCode,
        message: errorMessage,
      };
    }
  }

  /**
   * Get invitation details by public token (no auth required)
   */
  static async getInvitationByToken(token: string): Promise<InvitationDetails> {
    try {
      return await apiClient.get<InvitationDetails>(`/invite/${token}`);
    } catch (error: unknown) {
      const axiosError = error as AxiosErrorResponse;
      const errorCode =
        (axiosError.response?.data?.code as string) || "UNKNOWN_ERROR";
      const errorMessage =
        (axiosError.response?.data?.message as string) ||
        "Failed to load invitation";

      throw {
        code: errorCode,
        message: errorMessage,
      };
    }
  }

  /**
   * Verify invitation for logged in user
   * Cookies are automatically sent by apiClient with withCredentials: true
   */
  static async verifyInvitationForUser(
    invitationToken: string,
  ): Promise<InvitationDetails> {
    try {
      return await apiClient.get<InvitationDetails>(
        `/invite/${invitationToken}/verify`,
      );
    } catch (error: unknown) {
      const axiosError = error as AxiosErrorResponse;
      const errorCode =
        (axiosError.response?.data?.code as string) || "UNKNOWN_ERROR";
      const errorMessage =
        (axiosError.response?.data?.message as string) ||
        "Failed to verify invitation";

      throw {
        code: errorCode,
        message: errorMessage,
      };
    }
  }

  /**
   * Accept invitation by token
   * Cookies are automatically sent by apiClient with withCredentials: true
   */
  static async acceptInvitationByToken(
    invitationToken: string,
  ): Promise<AcceptInvitationByTokenResponse> {
    try {
      return await apiClient.post<AcceptInvitationByTokenResponse>(
        `/invite/${invitationToken}/accept`,
        {},
      );
    } catch (error: unknown) {
      const axiosError = error as AxiosErrorResponse;
      const errorCode =
        (axiosError.response?.data?.code as string) || "UNKNOWN_ERROR";
      const errorMessage =
        (axiosError.response?.data?.message as string) ||
        "Failed to accept invitation";

      throw {
        code: errorCode,
        message: errorMessage,
      };
    }
  }

  /**
   * Reject invitation by token
   * Cookies are automatically sent by apiClient with withCredentials: true
   */
  static async rejectInvitationByToken(
    invitationToken: string,
  ): Promise<RejectInvitationResponse> {
    try {
      return await apiClient.post<RejectInvitationResponse>(
        `/invite/${invitationToken}/reject`,
        {},
      );
    } catch (error: unknown) {
      const axiosError = error as AxiosErrorResponse;
      const errorCode =
        (axiosError.response?.data?.code as string) || "UNKNOWN_ERROR";
      const errorMessage =
        (axiosError.response?.data?.message as string) ||
        "Failed to reject invitation";

      throw {
        code: errorCode,
        message: errorMessage,
      };
    }
  }
}
